import { getSdk } from "../../services/sdkService";
import { i18n } from "../../services/i18nService";
import { config } from "../../config/config";
import { resetTimers } from "./keyHandler";
import { getErrorCode } from "../../errors/codes";
import { getAppState } from "../../state/index";
import { getLogger } from "../../utils/logger";

const htmlTag = document.getElementsByTagName("html")[0];
const Logger = getLogger();
const timeoutErrors = {
  bufferTimeoutError: "bufferTimeoutError",
  bufferTimeoutOfflineError: "bufferTimeoutOfflineError",
  loadTimeoutError: "loadTimeoutError"
};
let prevThumbnailIndex = null;
let playerLoadTimer = null;
let playerBufferingTimer = null;
let hideControlsTimer = null;
let pauseScreenTimer = null;
let sdk;
(async function getSdkInstance() {
  sdk = await getSdk();
})(); // IIFE (Immediately Invoked Function Expression)

/**
 * An enum listing the different playback states that can impact the UI presentation
 * @type {
 *   {
 *     PLAY: string,
 *     PAUSE: string
 *   }
 * }
 */
export const playbackStates = {
  INIT: "init",
  PLAY: "play",
  PAUSE: "pause",
  STOP: "stop"
};

/**
 * An enum listing the different player events that can impact the UI presentation
 * @type {
 *   {
 *     TIME_CHANGE: string,
 *     SUBTITLE_CHANGE: string,
 *     FINISH: string
 *   }
 * }
 */
export const playerEvents = {
  TIME_CHANGE: "timeChange",
  SUBTITLE_CHANGE: "subtitleChange",
  BUFFER_START: "bufferStart",
  BUFFER_END: "bufferEnd",
  FINISH: "finish",
  STOP: "stop",
  ERROR: "error",
  STATE: "state",
  SEEK: "seek"
  // SEEKED: 'seeked',
};

/**
 * The states on which the player can be in a given moment, which will determine what visual elements are displayed
 * on the screen.
 * @property {string} NONE  No UI will be visible other than the video itself
 * @property {string} CONTROLS_PLAYING  The video controls and settings buttons are visible, having the play/pause icon
 * in PAUSE status
 * @property {string} CONTROLS_PAUSED The video controls and settings buttons are visible, having the play/pause icon
 * in PLAY status
 * @property {string} TRACKS  The player shows the multi audio and subtitles panel
 * @property {string} EPISODE_SELECTOR  The player shows the series episodes selector, doesn't apply for movies
 * @property {string} PAUSE The pause screen with information about the title is visible
 * @property {string} FILMSTRIP The filmstrip carousel is visible and the user can interact with it
 * @property {string} NEXT_EPISODE  After episode completes a screen with a countdown is displayed
 * @property {string} MEDIA_KEYS The play/pause/stop/ff/rw buttons are pressed
 * @property {string} SKIP_INTRO The skip intro button is showing
 */
export const playerUiStates = {
  NONE: "none",
  CONTROLS_PLAYING: "controlsPlaying",
  CONTROLS_PAUSED: "controlsPaused",
  TRACKS: "tracks",
  EPISODE_SELECTOR: "episodeSelector",
  PAUSE: "pause",
  FILMSTRIP: "filmstrip",
  NEXT_EPISODE: "nextEpisode",
  MEDIA_KEYS: "mediaKeys",
  SKIP_INTRO: "skipIntro",
  SKIP_OUTRO: "skipOutro"
};

function stopTimers() {
  destroyUiHideTimeout.call(this);
  destroyPauseScreenTimer.call(this);
}

function getCurrentFilmstripPosition(timestamp) {
  const index = this.state.filmstripData.thumbnails.findIndex(thumb => {
    return thumb.timestamp >= timestamp;
  });
  prevThumbnailIndex = index;
  return index >= 0
    ? index >= this.state.filmstripData.thumbnails.length - 1
      ? this.state.filmstripData.thumbnails.length - 1
      : index
    : this.state.filmstripData.thumbnails.length - 1;
}

function showFilmstrip() {
  const currentTimestamp = this.getCurrentTimestamp();
  // TODO think about the first filmstrip case, or the case when the timestamp is exactly the current time
  const currentFilmstripPosition = getCurrentFilmstripPosition.call(this, currentTimestamp);
  this.filmstrip && this.filmstrip.goTo(currentFilmstripPosition);
  this.setState({
    currentFilmstripPosition
  });
}

/**
 * List of callbacks that will be executed after a new state is entered. These callbacks will be called by React's
 * componentDidUpdate function so the component's state will already be in sync with the state whose function is called.
 */
export const onStateEnter = {
  none: function() {
    destroyUiHideTimeout.call(this);
    resetTimers.call(this);
    this.state.playbackState === playbackStates.PAUSE && resetPauseScreenTimer.call(this);
  },
  controlsPlaying: function() {
    //this.state.playbackState === playbackStates.PAUSE && sdk.playerManager.play();
    setPlayerUiHideTimeout.call(this);
    destroyPauseScreenTimer.call(this);
  },
  controlsPaused: function() {
    destroyUiHideTimeout.call();
  },
  play: function() {
    this.setState({
      playerBuffering: false,
      canShowControls: true
    });
    setPlayerUiHideTimeout.call(this);
    destroyPauseScreenTimer.call(this);
  },
  pause: function() {
    //sdk.playerManager.pause();
  },
  tracks: function() {
    stopTimers.call(this);
  },
  episodeSelector: function() {
    //sdk.playerManager.pause();
    stopTimers.call(this);
  },
  filmstrip: function() {
    if (this.state.filmstripData) {
      showFilmstrip.call(this);
    }
    // if (this.playerPausFSEvent) {
    //   clearTimeout(this.playerPausFSEvent);
    // }
    // this.playerPausFSEvent = setTimeout(() => sdk.playerManager.pause(false), 1000);

    stopTimers.call(this);
  },
  mediaKeys: function() {
   // sdk.playerManager.pause(false);
    stopTimers.call(this);
  },
  nextEpisode: function() {
    destroyPauseScreenTimer.call(this);
  }
};

/**
 * List of callbacks that will be executed before a new state is entered. These callbacks will be called before calling
 * React's setState function so the component's state will still be in the state being left.
 */
const onStateExit = {
  filmstrip: function() {
    this.setState({
      currentFilmstripPosition: null,
      tentativePosition: null,
      subtitleText: ""
    });
  },
  mediaKeys: function() {
    this.setState({
      tentativePosition: null,
      subtitleText: ""
    });
  },
  nextEpisode: function() {
    this.setState({
      nextEpisodeCountDown: config.get().player.nextEpisodeCountDown
    });
  }
};

/**
 * Moved the player to the specified state after executing the onStateExit function of the current state. No transition
 * will happen if the requested new state is already the current state.
 * @param newState
 * @param callback
 */
export function changeState(newState, callback) {
  const shouldAvoidChangeState =
    this.config.isLive &&
    (newState === playerUiStates.FILMSTRIP ||
      newState === playerUiStates.MEDIA_KEYS ||
      newState === playerUiStates.PAUSE);
  if (this.state.activePanel !== newState && !shouldAvoidChangeState) {
    if (onStateExit[this.state.activePanel]) {
      onStateExit[this.state.activePanel].call(this);
    }
    this.setState(
      {
        activePanel: newState
      },
      callback
    );
  }
}

export function setPlayerUiHideTimeout() {
  destroyUiHideTimeout.call(this);
  hideControlsTimer = setTimeout(() => {
    this.state.playbackState !== playbackStates.PAUSE && changeState.call(this, playerUiStates.NONE);
  }, config.get().player.timeToHideUI);
}

export function destroyUiHideTimeout() {
  clearTimeout(hideControlsTimer);
  hideControlsTimer = null;
}

export function resetPauseScreenTimer() {
  destroyPauseScreenTimer.call(this);
  this.state.playbackState === playbackStates.PAUSE && setPauseScreenTimer.call(this);
}

export function setPauseScreenTimer() {
  destroyPauseScreenTimer.call(this);
  if (this.state.description) {
    pauseScreenTimer = setTimeout(() => {
      this.state.playbackState === playbackStates.PAUSE && changeState.call(this, playerUiStates.PAUSE);
    }, config.get().player.timeToShowPauseScreen);
  }
}

export function destroyPauseScreenTimer() {
  clearTimeout(pauseScreenTimer);
}

export function startNextEpisodeCountDown(nextEpisode) {
  this.setState({
    nextEpisodeCountDown: config.get().player.nextEpisodeCountDown
  });
  this.nextEpisodeTimer = setInterval(() => {
    if (this.state.nextEpisodeCountDown <= 1) {
      this.playEpisode(nextEpisode, true);
      changeState.call(this, playerUiStates.NONE);
      clearNextEpisodeCountDown.call(this);
      return;
    }
    this.setState({
      nextEpisodeCountDown: this.state.nextEpisodeCountDown - 1
    });
  }, 1000);
}

export function clearNextEpisodeCountDown() {
  clearInterval(this.nextEpisodeTimer);
}

function sendLoadTimeoutErrorReport() {
  const { playerManager, reportsManager } = sdk;
  const code = getErrorCode(timeoutErrors.loadTimeoutError);
  reportsManager.sendReport(
    { level: "error", event: "Playback", env: config.get().sdk.environment },
    {
      eventId: "PlayerError",
      report: code,
      status: "error",
      parameter: {
        errorCode: code,
        playbackInfo: {
          titleId: this.config.id,
          manifestUrl: this.config.manifest,
          releasePid: this.config.releasePids && this.config.releasePids[0],
          releaseUrl: this.config.releaseUrl,
          quality: this.config.playbackAnalytics.is4KEnabled,
          playerErrorDump: {
            trace: "Content loading timed out",
            placeInCode: "APP - playerStates.js"
          }
        }
      }
    }
  );
  playerManager.sendPlaybackAnalyticsEvent(playerManager.analyticsEvents.error, {
    code,
    name: "Content loading timed out"
  });
}

export function setPlayerLoadTimer() {
  playerLoadTimer = setTimeout(() => {
    Logger.log("setPlayerLoadTimer called, showing popup...", "PlayerStates");
    if (this.mounted) {
      // FIXME Load timer is not started because this.mounted is undefined
      const online = !!getAppState().isOnline;
      this.didErrorHappen = true;
      destroyBufferingTimer.call(this);
      this.props.resumeNavigation();
      this.setState({
        errorMessage: i18n.get(online ? "global.errors.timeout.text" : "global.errors.internetConnectivity.text"),
        errorMessageHeading: i18n.get("global.errors.internetConnectivity.title")
      });
      // Important: this must happen before stopping the player. We need the reporting to happen before playback ends
      online && sendLoadTimeoutErrorReport.call(this);
      sdk.playerManager.stop();
      sdk.playerManager.destroy();
      this.props.setFocus(this.errorButtonModalPath);
    }
  }, config.get().player.loadTimeout);
}

export function destroyPlayerLoadTimer(isResettingErrorMessage = true) {
  isResettingErrorMessage &&
    this.setState({
      errorMessage: null,
      errorMessageHeading: null
    });
  Logger.log("Destroying player load timer", "Player");
  clearTimeout(playerLoadTimer);
  playerLoadTimer = null;
}

function sendBufferTimeoutErrorReport() {
  const { reportsManager, playerManager } = sdk;
  const { bufferTimeoutError, bufferTimeoutOfflineError } = timeoutErrors;
  const online = !!getAppState().isOnline;
  const errorCode = online ? bufferTimeoutError : bufferTimeoutOfflineError;
  const code = getErrorCode(errorCode);
  if (!online) {
    this.playerErrorHandler({ code, data: { name: "Player Network Disconnection" } });
  } else {
    reportsManager.sendReport(
      { level: "error", event: "Playback", env: config.get().sdk.environment },
      {
        eventId: "PlayerError",
        report: code,
        status: "error",
        parameter: {
          errorCode: code,
          playbackInfo: {
            titleId: this.config.id,
            manifestUrl: this.config.manifest,
            releasePid: this.config.releasePids && this.config.releasePids[0],
            releaseUrl: this.config.releaseUrl,
            quality: this.config.playbackAnalytics.is4KEnabled,
            playerErrorDump: {
              trace: "Content buffering timed out",
              placeInCode: "APP - playerStates.js"
            }
          }
        }
      }
    );
  }
  playerManager.sendPlaybackAnalyticsEvent(playerManager.analyticsEvents.error, {
    code,
    name: "Content buffering timed out"
  });
}

export function setBufferingTimer() {
  clearTimeout(playerBufferingTimer);
  playerBufferingTimer = setTimeout(() => {
    Logger.log("setBufferingTimer called, showing popup...", "PlayerStates");
    const online = !!getAppState().isOnline;
    this.didErrorHappen = true;
    destroyPlayerLoadTimer.call(this);
    this.props.resumeNavigation();
    this.setState({
      errorMessage: i18n.get(online ? "global.errors.timeout.text" : "global.errors.internetConnectivity.text"),
      errorMessageHeading: i18n.get("global.errors.internetConnectivity.title")
    });
    // Important: this must happen before stopping the player. We need the reporting to happen before playback ends
    sendBufferTimeoutErrorReport.call(this);
    sdk.playerManager.stop();
    sdk.playerManager.destroy();
    this.props.setFocus(this.errorButtonModalPath);
    getAppState().avoidNetworkCalls = true;
  }, config.get().player.bufferingTimeout);
}

export function destroyBufferingTimer(isResettingErrorMessage = true) {
  isResettingErrorMessage &&
    this.setState({
      errorMessage: null,
      errorMessageHeading: null
    });
  Logger.log("Destroying buffering timer", "Player");
  clearTimeout(playerBufferingTimer);
  playerBufferingTimer = null;
}

export function showPlayer() {
  htmlTag.classList.add("isPlayerVisible");
}

export function hidePlayer() {
  htmlTag.classList.remove("isPlayerVisible");
}
